import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadQuotes from "@insite/client-framework/Store/Data/Quotes/Handlers/LoadQuotes";
import { getQuotesDataView } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CardContainer, { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import CardList, { CardListStyles } from "@insite/content-library/Components/CardList";
// import CardListHeading from "@insite/content-library/Components/CardListHeading"; // Custom Code
import QuoteSummaryCard, { QuoteSummaryCardStyles } from "@insite/content-library/Components/QuoteSummaryCard";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

/* Custom Code - Start */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import { DataTableBody, DataTableHead, DataTableHeader } from "@insite/mobius/index";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import LoadingOverlay, { LoadingOverlayProps } from "@store/MobiusOverrides/LoadingOverlay/LoadingOverlay";
import CardListHeading from "@store/Overrides/Components/CardListHeading";
import { historyTableProps } from "@store/Themes/common/props/history";
import { typographyGetNoResultsProps } from "@store/Themes/common/props/typography";
/* Custom Code - End */

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const isAuthenticated = session.isAuthenticated && !session.isGuest;
    return {
        settingsCollection: getSettingsCollection(state),
        session,
        quotesDataView: getQuotesDataView(state, recentQuotesParameter),
        myQuotesPageNavLink: getPageLinkByPageType(state, "RfqMyQuotesPage"),
        canViewQuotes: isAuthenticated && (session.userRoles || "").indexOf("Requisitioner") === -1,
    };
};

const mapDispatchToProps = {
    loadQuotes,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RecentQuotesStyles {
    cardList?: CardListStyles;
    noQuotesGridItem?: GridItemProps;
    noQuotesText?: TypographyPresentationProps;
    cardContainer?: CardContainerStyles;
    quoteSummaryCard?: QuoteSummaryCardStyles;
    // Custom Code
    wrapper?: InjectableCss;
    loadingOverlay?: LoadingOverlayProps;
    tableWrapper?: InjectableCss;
    dataTable?: DataTableProps;
    quoteNumberHeader?: DataTableHeaderProps;
    typeHeader?: DataTableHeaderProps;
    statusHeader?: DataTableHeaderProps;
    requestedDateHeader?: DataTableHeaderProps;
    expirationDateHeader?: DataTableHeaderProps;
    salesRepHeader?: DataTableHeaderProps;
    userHeader?: DataTableHeaderProps;
    customerRepHeader?: DataTableHeaderProps;
    shipToHeader?: DataTableHeaderProps;
}

export const recentQuotesStyles: RecentQuotesStyles = {
    loadingOverlay: { minHeight: 80 },
    noQuotesText: typographyGetNoResultsProps(),
    tableWrapper: historyTableProps.wrapper,
    dataTable: historyTableProps.dataTable,
    quoteNumberHeader: historyTableProps.orderNumberHeader,
    requestedDateHeader: historyTableProps.dateHeader,
    expirationDateHeader: historyTableProps.dateHeader,
    shipToHeader: historyTableProps.shipToHeader,
};

const recentQuotesParameter = {
    page: 1,
    pageSize: 5,
};

const styles = recentQuotesStyles;

class RecentQuotes extends React.Component<Props> {
    componentDidMount() {
        if (this.props.canViewQuotes && !this.props.quotesDataView.value) {
            this.props.loadQuotes(recentQuotesParameter);
        }
    }

    render() {
        const { settingsCollection, session, quotesDataView, myQuotesPageNavLink, canViewQuotes } = this.props;
        if (!canViewQuotes /* Removed - !quotesDataView.value */) {
            return null;
        }

        const myQuotesPageUrl = myQuotesPageNavLink ? myQuotesPageNavLink.url : undefined;

        /* Custom Code - Start */
        const hasNoResults = !quotesDataView.value || quotesDataView.value.length === 0;

        return (
            <StyledWrapper {...styles.wrapper}>
                <CardListHeading
                    heading={translate("Recent Quotes")}
                    viewAllUrl={myQuotesPageUrl}
                    hasNoResults={hasNoResults}
                />
                <LoadingOverlay {...styles.loadingOverlay} loading={quotesDataView.isLoading}>
                    {quotesDataView.value &&
                        (hasNoResults ? (
                            <Typography {...styles.noQuotesText}>{siteMessage("Rfq_NoQuotesMessage")}</Typography>
                        ) : (
                            <StyledWrapper {...styles.tableWrapper}>
                                <DataTable {...styles.dataTable}>
                                    <DataTableHead>
                                        <DataTableHeader {...styles.quoteNumberHeader}>
                                            {translate("Quote #")}
                                        </DataTableHeader>
                                        <DataTableHeader {...styles.typeHeader}>{translate("Type")}</DataTableHeader>
                                        <DataTableHeader {...styles.statusHeader}>
                                            {translate("Status")}
                                        </DataTableHeader>
                                        <DataTableHeader {...styles.requestedDateHeader}>
                                            {translate("Requested")}
                                        </DataTableHeader>
                                        <DataTableHeader {...styles.expirationDateHeader}>
                                            {translate("Expires")}
                                        </DataTableHeader>
                                        {session.isSalesPerson && (
                                            <>
                                                <DataTableHeader {...styles.salesRepHeader}>
                                                    {translate("Sales Rep")}
                                                </DataTableHeader>
                                                <DataTableHeader {...styles.userHeader}>
                                                    {translate("User")}
                                                </DataTableHeader>
                                            </>
                                        )}
                                        <DataTableHeader {...styles.customerRepHeader}>
                                            {translate("Customer")}
                                        </DataTableHeader>
                                        {!session.isSalesPerson && (
                                            <DataTableHeader {...styles.shipToHeader}>
                                                {translate("Ship To / Pick Up")}
                                            </DataTableHeader>
                                        )}
                                    </DataTableHead>
                                    <DataTableBody>
                                        {quotesDataView.value.map(quote => (
                                            <QuoteSummaryCard
                                                key={quote.id}
                                                quote={quote}
                                                session={session}
                                                quoteSettings={settingsCollection.quoteSettings}
                                                extendedStyles={styles.quoteSummaryCard}
                                            />
                                        ))}
                                    </DataTableBody>
                                </DataTable>
                            </StyledWrapper>
                        ))}
                </LoadingOverlay>
            </StyledWrapper>
        );
        /* Custom Code - End */
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RecentQuotes),
    definition: {
        group: "Common",
        icon: "list-ul",
    },
};

export default widgetModule;
